import { useState, useEffect, useContext } from 'react';
import { Grid, Typography, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper, Button, IconButton, CircularProgress, Modal, Box } from '@mui/material';
import CartSummary from '../components/cart/CartSummary';
import RemoveShoppingCartIcon from '@mui/icons-material/RemoveShoppingCart';
import removeItemFromCart from '../hooks/cart/removeItemFromCart';
import addCoupon from '../hooks/coupon/addCoupon';
import getCouponId from '../hooks/coupon/getCouponId';
import CartContext, { useCart } from '../contexts/CartContext';
import CustomSnackbar from '../components/elements/CustomSnackbar';
import { useNavigate } from 'react-router-dom';
import createOrder from '../hooks/cart/createOrder';
import { KEY_CHARGE_ONE_TIME_PAYMENT, KEY_COUPON_LOCATION_ID_ALL, KEY_ORDER_TYPE_DEPOSIT, KEY_ORDER_TYPE_FULL } from '../constants';
import { useUser } from '../contexts/UserContext';
import removeAllItemsFromCart from '../hooks/cart/removeAllItemsFromCart';
import { CartItem, CartItemType, PricingDiscountTypes, OrderErrors } from '../types';
import { calculateDiscountedPrice, formatDate, getChildrenInMultipleEventsAtTheSameTime } from '../utils';
import Countdown from '../components/cart/Countdown';
declare const window: any;

const CartPage = () => {
    const { depositTotal, applyCoupon, setOrderType, fetchCart, payInFullTotal, depositTotalWithDiscounts } = useCart();
    const { cartItems } = useContext(CartContext) as unknown as { cartItems: CartItemType };
    const navigate = useNavigate();
    const { user } = useUser();
    const [loading, setLoading] = useState(false);
    const [buttonDisabled, setButtonDisabled] = useState(false);
    const [snackbar, setSnackbar] = useState<{open: boolean, message: string, severity: string, linkLabel?: string, linkOnClick?: () => void}>({
        open: false,
        message: '',
        severity: 'info',
    });

    const [multipleEventsConfirmModa, setMultipleEventsConfirmModa] = useState<{ isOpen: boolean, type: string, data: any }>({ isOpen: false, type: KEY_ORDER_TYPE_FULL, data: [] });

    const handleMultipleEventsConfirmModaOpen = (type: string) => {
        let children = getChildrenInMultipleEventsAtTheSameTime(cartItems.data);
        if (Object.keys(children).length === 0) {
            handleCreateOrder(type);
            return;
        }
        setMultipleEventsConfirmModa({ isOpen: true, type, data: Object.values(children) })
    };
    const handleMultipleEventsConfirmModaClose = () => setMultipleEventsConfirmModa({ ...multipleEventsConfirmModa, isOpen: false });
    const handleMultipleEventsConfirmModaConfirm = () => {
        setMultipleEventsConfirmModa({ ...multipleEventsConfirmModa, isOpen: false });
        handleCreateOrder(multipleEventsConfirmModa.type);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    useEffect(() => {
        if (!user) {
            navigate('/');
        }
    }, [user]);

    useEffect(() => {
        const initFetchCart = async () => {
            setLoading(true);
            try {
                const responseCart = await fetchCart();
                if (!responseCart) {
                    setSnackbar({ open: true, message: "Failed to retrieve cart", severity: 'error' });
                }
            } catch (error) {
                setSnackbar({ open: true, message: "Failed to retrieve cart", severity: 'error' });
            } finally {
                setLoading(false);
            }
        };

        initFetchCart();
    }, []);

    useEffect(() => {
        if (cartItems) {
            if (cartItems.invalidCoupons && cartItems.invalidCoupons.length > 0) {
                setSnackbar({ open: true, message: "A coupon expired.", severity: 'info' });
            }
            if (cartItems.invalidItems && cartItems.invalidItems.length > 0) {
                setSnackbar({ open: true, message: "Some item(s) have been removed from your cart because they are no longer available.", severity: 'info' });
            }
            if (cartItems.invalidMessages && cartItems.invalidMessages.length > 0) {
                setSnackbar({ open: true, message: cartItems.invalidMessages.join('; '), severity: 'error' });
            }
            if (cartItems.cartMessages && cartItems.cartMessages.length > 0) {
                setSnackbar({ open: true, message: cartItems.cartMessages.join('; '), severity: 'info' });
            }
        }
    }, [cartItems]);

    const handleRemoveItemFromCart = async (pricingId: any) => {
        setLoading(true);
        try {
            const success = await removeItemFromCart(pricingId);
            if (success) {
                await fetchCart();
                setSnackbar({ open: true, message: "Item removed from cart", severity: 'success' });
            } else {
                setSnackbar({ open: true, message: "Failed to remove item from cart", severity: 'error' });
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Failed to remove item from cart", severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveAllItemsFromCart = async () => {
        setLoading(true);
        try {
            const removeAllItemsFromCartResponse = await removeAllItemsFromCart();
            if (removeAllItemsFromCartResponse) {
                await fetchCart();
                setSnackbar({ open: true, message: 'Cart cleared successfully.', severity: 'success' });
            } else {
                setSnackbar({ open: true, message: 'Failed to remove all items from cart.', severity: 'error' });
            }
        } catch (error) {
            setSnackbar({ open: true, message: 'An error occurred while deleting all items from cart.', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const applyCouponCode = async (couponCode: string) => {
        try {
            const couponResponse = await getCouponId(couponCode);

            if (couponResponse.success) {
                const addCouponResponse = await addCoupon(couponCode);

                if (addCouponResponse.success) {
                    setSnackbar({ open: true, message: 'Coupon applied successfully.', severity: 'success' });
                    if (couponResponse.data) {
                        applyCoupon({ 
                            code: couponResponse.data.id, 
                            value: couponResponse.data.amount, 
                            type: couponResponse.data.type, 
                            locationId: couponResponse.data.locationId, 
                            locationName: couponResponse.data.locationId == KEY_COUPON_LOCATION_ID_ALL ? '' : couponResponse.data.location?.name ?? ''
                        });
                    }

                    await fetchCart();
                    return true;
                } else {
                    setSnackbar({ open: true, message: 'Failed to apply coupon.', severity: 'error' });
                    return false;
                }
            } else {
                setSnackbar({ open: true, message: 'Coupon not found.', severity: 'error' });
                return false;
            }
        } catch (error) {
            setSnackbar({ open: true, message: 'An error occurred while applying the coupon.', severity: 'error' });
            return false;
        }
    };

    const handleCreateOrder = async (orderType: string) => {
        setButtonDisabled(true);
        setOrderType(orderType);
        const orderResponse = await createOrder();

        if (orderResponse.success) {
            if (orderResponse.message) {
                setSnackbar({ open: true, message: orderResponse.message, severity: 'info' });
                await new Promise(resolve => setTimeout(resolve, 6000));
                await fetchCart();
            }
            setButtonDisabled(false);
            navigate('/checkout');
        } else {
            setButtonDisabled(false);
            if (orderResponse?.message === OrderErrors.DUPLICATE_CHARGE_PREVENTED)
                setSnackbar({ 
                    open: true, 
                    message: 'Cart contains items you have already purchased. Please contact us at support@kindertales.com or use the chat bubble.',
                    severity: 'error',
                })
            else
                setSnackbar({ open: true, message: orderResponse.message || 'An error occurred while creating order.', severity: 'error' });
            return false;
        }
    };

    return (
        <div className="event-page p-5 mb-20">
            <Grid container justifyContent="center" spacing={2} className="pt-20 cart">
                <Grid item xs={12} md={1}></Grid>
                <Grid container item xs={12} md={10}>
                    <Grid item xs={12} md={12}>
                        <Typography variant="h4" align="center" className="cart-header">
                            Your Cart {loading ? '' : cartItems.data ? '(' + cartItems.data.length + ' items)' : ''}
                        </Typography>
                    </Grid>
                    <Grid container item>
                        {loading ? (
                            <Grid container item xs={12} md={12} justifyContent="center" alignItems="center">
                                <div className="text-center pt-20">
                                    <CircularProgress />
                                </div>
                            </Grid>
                        ) : (
                            <Grid container item xs={12} md={12}>
                                <Grid item xs={12} className="mt-65">
                                    <TableContainer component={Paper}>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Item</TableCell>
                                                    <TableCell>Deposit Total</TableCell>
                                                    <TableCell>Remaining Tuition</TableCell>
                                                    <TableCell>Total Tuition</TableCell>
                                                    <TableCell sx={{ fontSize: '14px !important' }}>Countdown</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {cartItems.data !== undefined && cartItems.data.length > 0 && cartItems.data.map((cartItem, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell sx={{ textAlign: 'left !important' }}>
                                                            <span className="item-header">
                                                                {`${cartItem.item.event.name} for ${cartItem.child?.firstName} ${cartItem.child?.lastName}`}
                                                                <br />
                                                                <span className="location-name">{cartItem.item.event.location?.name ?? ''}</span>
                                                                <br />
                                                                <Button onClick={() => handleRemoveItemFromCart(cartItem.id)} className="btn-remove pl-0">
                                                                    Remove
                                                                </Button>
                                                            </span>
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="pricing-details">
                                                                {cartItem.discountAmountDeposit ? (
                                                                    <>
                                                                        <span className="original-price price-detail crossed-out">
                                                                            ${parseFloat(cartItem.item.depositAmount || 0).toFixed(2)}
                                                                        </span>
                                                                        <span className="discounted-price price-detail bold">
                                                                            ${(parseFloat(cartItem.item.depositAmount || 0) - parseFloat(cartItem.discountAmountDeposit)).toFixed(2)}
                                                                        </span>
                                                                    </>
                                                                ) : (<>
                                                                        <span className="original-price price-detail bold">
                                                                            ${parseFloat(cartItem.item.depositAmount || 0).toFixed(2)}
                                                                        </span>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <span className="item-pricing">
                                                                ${((parseFloat(cartItem.item.chargePerPeriod || 0) - parseFloat(cartItem.item.depositAmount || 0)).toFixed(2))}<br />
                                                                <b>{cartItem.item.chargeType.id === KEY_CHARGE_ONE_TIME_PAYMENT ? (cartItem.item.autoPaymentDate ? "Charged on " + (formatDate(cartItem.item.autoPaymentDate)) : '') : cartItem.item.chargeType.description}</b>
                                                            </span>
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="pricing-details">
                                                                {cartItem.discountAmountCoupon ? (
                                                                    <>
                                                                        <span className="original-price price-detail crossed-out">
                                                                            ${parseFloat(cartItem.item.chargePerPeriod || '0').toFixed(2)}
                                                                        </span>
                                                                        <span className="discounted-price price-detail bold">
                                                                            ${(parseFloat(cartItem.item.chargePerPeriod || '0') - parseFloat(cartItem.discountAmountCoupon)).toFixed(2)}
                                                                        </span>
                                                                    </>
                                                                ) : (
                                                                    <span className="original-price price-detail bold">
                                                                        ${parseFloat(cartItem.item.chargePerPeriod || '0').toFixed(2)}
                                                                    </span>
                                                                )}
                                                                <br />
                                                                {cartItem.item.discountType !== undefined && cartItem.item.discountType != PricingDiscountTypes.DISABLED && (
                                                                    <span className="item-pricing paid-full" style={{ color: 'green' }}>
                                                                        ${calculateDiscountedPrice(
                                                                            cartItem.item.chargePerPeriod,
                                                                            cartItem.item.discountType,
                                                                            cartItem.item.discountAmount,
                                                                            cartItem.discountAmountCoupon
                                                                        )} if paid in full
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Countdown heldUntil={cartItem.heldUntil}/>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Grid>
                                <Grid item xs={12} md={12} className="pt-5">
                                    {cartItems && cartItems.data?.length > 0 && (
                                        <>
                                            <IconButton className="ptf-0 ptb-0 btn-remove">
                                                <RemoveShoppingCartIcon />
                                            </IconButton>

                                            <Button onClick={handleRemoveAllItemsFromCart} className="btn-remove">
                                                Remove All Items
                                            </Button>
                                        </>
                                    )}
                                </Grid>
                                <Grid item xs={12} md={6}></Grid>
                                {cartItems && cartItems.data?.length > 0 && (
                                    <>
                                        <Grid item xs={12} md={6} className="pt-10">
                                            <CartSummary
                                                applyCouponCode={applyCouponCode}
                                                readonly={false}
                                                checkout={false}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}></Grid>
                                        <Grid item xs={12} md={6} container className="pt-20 pb-20" spacing={4}>
                                            <Grid item xs={12} md={6}>
                                                {Number(depositTotal.toFixed(2)) > 0 && (
                                                    <Button 
                                                        variant="contained" 
                                                        color="primary" 
                                                            onClick={() => handleMultipleEventsConfirmModaOpen(KEY_ORDER_TYPE_DEPOSIT)} 
                                                        className="btn blue"
                                                        disabled={buttonDisabled}
                                                    >
                                                            Pay Deposit Only - ${depositTotalWithDiscounts.toFixed(2)}
                                                        </Button>
                                                )}
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Button 
                                                    variant="contained" 
                                                    color="primary" 
                                                        onClick={() => handleMultipleEventsConfirmModaOpen(KEY_ORDER_TYPE_FULL)} 
                                                    className="btn white"
                                                    disabled={buttonDisabled}
                                                >
                                                    Pay Grand Total - ${Number(payInFullTotal.toFixed(2)) > 0 ? payInFullTotal.toFixed(2) : '0.00'}
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        )}
                    </Grid>
                </Grid>
                <Grid item xs={12} md={1}></Grid>
                <CustomSnackbar
                    open={snackbar.open}
                    message={snackbar.message}
                    severity={snackbar.severity}
                    handleClose={handleCloseSnackbar}
                    linkLabel={snackbar.linkLabel ? snackbar.linkLabel : undefined}
                    linkOnClick={snackbar.linkOnClick ? snackbar.linkOnClick : undefined}
                />
            </Grid>
            <Modal
                open={multipleEventsConfirmModa.isOpen}
                onClose={handleMultipleEventsConfirmModaClose}
                aria-labelledby="modal-title"
                aria-describedby="modal-description"
            >
                <Box sx={{ p: 4, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 24, width: 300, margin: 'auto', mt: '20%' }}>
                    <Typography id="modal-title" variant="h6" component="h6">
                        This child is already registered for another event that overlaps with the selected dates. You may proceed, but please note this could result in a double booking.
                    </Typography>
                    {
                        multipleEventsConfirmModa?.data?.length && multipleEventsConfirmModa.data.map((cartItems: CartItem[]) => (
                            <Typography id="modal-description" sx={{ mt: 2 }}>
                                Child Name: {cartItems[0].child.firstName} {cartItems[0].child.lastName}
                            </Typography>
                        ))
                    }
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button onClick={handleMultipleEventsConfirmModaClose} sx={{ mr: 1 }}>
                            Close
                        </Button>
                        <Button variant="contained" onClick={handleMultipleEventsConfirmModaConfirm}>
                            Confirm
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </div>
    );
};

export default CartPage;
